// Client for the video agent API (/api/v1/agent). Types mirror
// server/src/agent/types.ts — keep them in sync.

export type AttachmentKind = 'image' | 'video' | 'audio';

// Files per chat message (server: MAX_ATTACHMENTS_PER_MESSAGE).
export const MAX_ATTACHMENTS = 8;

export type Attachment = {
  kind: AttachmentKind;
  r2_key: string;
  url: string;
  name?: string;
  uploaded_at: string;
  // A multi-view reference sheet the agent composed (make_reference_sheet).
  sheet?: { subject: string; views: string[]; sources: string[]; cols: number; rows: number };
};

export type ToolCall = { id: string; name: string; arguments: string };

export type GenerationKind = 'video' | 'image' | 'image_edit';

export type Proposal = {
  kind: GenerationKind;
  title: string;
  credits: number;
  summary: Record<string, string | number>;
  request: Record<string, unknown>;
  // Video: the plan in the user's words, and how request.prompt was written.
  brief?: string;
  prompt_source?: 'h3-skill' | 'edited' | 'brief';
  // Video: the quote per tier at request.duration, for the card's tier switch.
  credits_by_tier?: Partial<Record<'turbo' | 'base', number>>;
};

export type EventContent =
  | { type: 'proposal_confirmed'; call_id: string; activity_id: number; kind: GenerationKind; credits: number }
  | { type: 'proposal_cancelled'; call_id: string }
  | { type: 'proposal_failed'; call_id: string; error: string; code?: 'content_rejected'; reason?: 'minor' | 'explicit' }
  | { type: 'job_finished'; call_id: string; activity_id: number; kind: GenerationKind; status: 'completed' | 'failed'; error?: string }
  // A turn crashed; the chat offers Retry while it is the latest turn.
  | { type: 'turn_failed'; error: string }
  // API runs only: the runner's own steps (reviews, outcomes), shown as status lines.
  | { type: 'run_event'; kind: 'run' | 'phase' | 'input' | 'job' | 'review' | 'variant' | 'cancel' | 'error'; message: string; variant?: number; iteration?: number; data?: Record<string, unknown> };

export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

// Server-side view of a submitted job (server/src/agent/jobs.ts).
export type JobSnapshot = {
  activity_id: number;
  status: JobStatus;
  error: string | null;
  created_at?: string;
  completed_at?: string | null;
  eta_seconds?: number;
  eta_updated_at?: string;
  queue_position?: number;
};

export type ActiveJob = JobSnapshot & {
  call_id: string;
  kind: GenerationKind;
  conversation_id: string;
  conversation_title: string;
  submitted_at: string;
};

export type StoredMessage =
  | {
      id: number;
      seq: number;
      role: 'user';
      content: { text: string; attachments?: Attachment[]; choice?: { call_id: string; option_id: string }; client_id?: string };
      created_at: string;
    }
  | {
      id: number;
      seq: number;
      role: 'assistant';
      content: { text: string; reasoning?: string; tool_calls?: ToolCall[] };
      created_at: string;
    }
  | { id: number; seq: number; role: 'tool'; content: { call_id: string; name: string; result: unknown }; created_at: string }
  | { id: number; seq: number; role: 'event'; content: EventContent; created_at: string };

export type Conversation = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  // List rows only (server ConversationSummary): a one-line gist of the chat
  // and its newest submitted generation.
  preview?: string;
  latest_job?: { activity_id: number; kind: GenerationKind };
  // An API run (POST /api/v1/agent/runs) shown as a read-only chat.
  origin?: 'chat' | 'api';
  read_only?: boolean;
  run_id?: string;
};

export type StreamEvent =
  | { type: 'message'; message: StoredMessage }
  | { type: 'reasoning'; delta: string }
  | { type: 'text'; delta: string }
  | { type: 'tool_start'; name: string }
  | { type: 'error'; error: string }
  | { type: 'done' };

export type OptionAction = 'reply' | 'upload_image' | 'upload_audio' | 'upload_video' | 'type';
export type AgentOption = { label: string; description?: string; action?: OptionAction; placeholder?: string };

const BASE = '/api/v1/agent';

async function json<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = body as { message?: string; error?: string };
    throw Object.assign(new Error(err.message ?? err.error ?? `HTTP ${res.status}`), { status: res.status, body });
  }
  return body as T;
}

export async function listConversations(): Promise<Conversation[]> {
  const r = await json<{ items: Conversation[] }>(await fetch(`${BASE}/conversations`, { credentials: 'include' }));
  return r.items;
}

export async function createConversation(): Promise<Conversation> {
  return json(
    await fetch(`${BASE}/conversations`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    })
  );
}

export async function getConversation(
  id: string
): Promise<{ conversation: Conversation; messages: StoredMessage[]; jobs?: Record<string, JobSnapshot> }> {
  return json(await fetch(`${BASE}/conversations/${id}`, { credentials: 'include' }));
}

// Jobs still rendering across all of the user's chats.
export async function listActiveJobs(): Promise<ActiveJob[]> {
  const r = await json<{ items: ActiveJob[] }>(await fetch(`${BASE}/jobs/active`, { credentials: 'include' }));
  return r.items;
}

export async function deleteConversation(id: string): Promise<void> {
  await json(await fetch(`${BASE}/conversations/${id}`, { method: 'DELETE', credentials: 'include' }));
}

export async function uploadAttachment(file: File, kind?: AttachmentKind): Promise<Attachment> {
  const form = new FormData();
  form.set('file', file);
  if (kind) form.set('kind', kind);
  return json(await fetch(`${BASE}/upload`, { method: 'POST', credentials: 'include', body: form }));
}

export function kindOfFile(file: File): AttachmentKind | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return null;
}

export type SendBody = {
  text: string;
  attachments?: Attachment[];
  choice?: { call_id: string; option_id: string };
  locale?: string;
  // Same id on a re-send = retry of that message, never a second copy.
  client_id?: string;
};

export function newClientId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// POSTs a user message and reads the agent's turn as server-sent events.
export async function sendMessage(conversationId: string, body: SendBody, onEvent: (e: StreamEvent) => void): Promise<void> {
  await streamTurn(`${BASE}/conversations/${conversationId}/messages`, JSON.stringify(body), onEvent);
}

// Re-runs the conversation's last turn after it failed (the stored user
// message is reused). 409 `nothing_to_retry` means it already finished.
export async function retryTurn(conversationId: string, onEvent: (e: StreamEvent) => void): Promise<void> {
  await streamTurn(`${BASE}/conversations/${conversationId}/retry`, '{}', onEvent);
}

async function streamTurn(url: string, body: string, onEvent: (e: StreamEvent) => void): Promise<void> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body,
  });
  if (!res.ok || !res.body) {
    await json(res);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const data = frame
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trimStart())
        .join('\n');
      if (!data) continue;
      try {
        onEvent(JSON.parse(data) as StreamEvent);
      } catch {
        /* ignore malformed frame */
      }
    }
  }
}

export async function confirmProposal(
  conversationId: string,
  callId: string,
  edits: { prompt?: string; duration?: number; tier?: string; fingerprint?: string }
): Promise<StoredMessage> {
  const res = await fetch(`${BASE}/conversations/${conversationId}/proposals/${encodeURIComponent(callId)}/confirm`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(edits),
  });
  const body = (await res.json().catch(() => ({}))) as { message?: StoredMessage; message_row?: StoredMessage; error?: string; reason?: string };
  if (!res.ok) {
    throw Object.assign(new Error((body as { message?: string }).message ?? body.error ?? `HTTP ${res.status}`), {
      status: res.status,
      row: body.message_row,
      code: body.error,
      reason: body.reason,
    });
  }
  return body.message!;
}

export async function cancelProposal(conversationId: string, callId: string): Promise<StoredMessage> {
  const r = await json<{ message: StoredMessage }>(
    await fetch(`${BASE}/conversations/${conversationId}/proposals/${encodeURIComponent(callId)}/cancel`, {
      method: 'POST',
      credentials: 'include',
    })
  );
  return r.message;
}

export function parseArgs(call: ToolCall): Record<string, unknown> {
  try {
    return JSON.parse(call.arguments) as Record<string, unknown>;
  } catch {
    return {};
  }
}
